/**
 * Leaguemate Scouting — S1 throttled, cached fetch orchestrator.
 *
 * Stays well under Sleeper's 1000-calls/min ceiling: a single queue spaces
 * request STARTS by FETCH_THROTTLE_MS (~500/min) and caps in-flight requests
 * at FETCH_CONCURRENCY, with exponential backoff on HTTP 429.
 *
 * Two cache tiers, mirroring the existing sleeperImport.js pattern (a
 * module-level in-memory memo) layered over the Almanac IndexedDB cache
 * (almanacCache.js — its own persistence, NOT a parallel store):
 *   1. `_mem`  — within-session dedupe by URL. Also the layer that makes a
 *                same-process second pass register as all-hits (IndexedDB is
 *                unavailable under Node, so the live smoke relies on this).
 *   2. getCached/setCached — cross-session IndexedDB persistence keyed by
 *                (cacheId, season, dataType). cacheId is the league_id for
 *                league endpoints and the user_id for /user/.../leagues.
 *
 * NOTE: unlike almanacCache.withCache, we do NOT bypass the current season —
 * scouting is fundamentally about current rosters, and re-runs must hit cache.
 */

import { getCached, setCached } from "../almanacCache.js";
import {
  FETCH_THROTTLE_MS,
  FETCH_CONCURRENCY,
  FETCH_MAX_RETRIES,
  FETCH_BACKOFF_BASE_MS,
} from "./scoutConfig.js";

export const SCOUT_BASE_URL = "https://api.sleeper.app/v1";

// ---- cache stats (per-run; caller resets at the start of a run) ----------
const _stats = { hits: 0, misses: 0 };
export function resetScoutCacheStats() { _stats.hits = 0; _stats.misses = 0; }
export function getScoutCacheStats() { return { ..._stats }; }

// ---- within-session in-memory memo ---------------------------------------
const _mem = new Map(); // url -> parsed JSON (or null)
export function clearScoutMemCache() { _mem.clear(); }

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ---- throttled, concurrency-capped dispatch queue ------------------------
// Spaces task STARTS by FETCH_THROTTLE_MS and runs at most FETCH_CONCURRENCY
// concurrently. Tasks are thunks returning a Promise.
const _queue = [];
let _active = 0;
let _lastStart = 0;

function _enqueue(thunk) {
  return new Promise((resolve, reject) => {
    _queue.push({ thunk, resolve, reject });
    _drain();
  });
}

function _drain() {
  if (_active >= FETCH_CONCURRENCY) return;
  const item = _queue.shift();
  if (!item) return;
  _active++;
  const now = Date.now();
  const wait = Math.max(0, _lastStart + FETCH_THROTTLE_MS - now);
  _lastStart = now + wait;
  setTimeout(async () => {
    try {
      item.resolve(await item.thunk());
    } catch (e) {
      item.reject(e);
    } finally {
      _active--;
      _drain();
    }
  }, wait);
  _drain(); // fill remaining concurrency slots (each spaced by THROTTLE_MS)
}

// ---- raw fetch with backoff ----------------------------------------------
async function _rawFetch(url) {
  for (let attempt = 0; attempt < FETCH_MAX_RETRIES; attempt++) {
    try {
      const r = await fetch(url);
      if (r.status === 429) {
        await _sleep(FETCH_BACKOFF_BASE_MS * 2 ** attempt); // exponential backoff
        continue;
      }
      if (r.status === 404) return null; // not found → treat as empty, don't retry
      if (!r.ok) { await _sleep(300); continue; }
      return await r.json();
    } catch {
      await _sleep(300);
    }
  }
  return null;
}

/**
 * Cached + throttled GET. Returns parsed JSON (or null).
 *
 * @param url      full Sleeper URL
 * @param opts.cacheId    IndexedDB cache partition (league_id or user_id); omit to skip IDB
 * @param opts.season     season token for the cache key
 * @param opts.dataType   cache data-type label (e.g. "scout_rosters")
 */
export async function scoutFetch(url, { cacheId, season, dataType } = {}) {
  // Tier 1: in-memory memo (URL-keyed).
  if (_mem.has(url)) { _stats.hits++; return _mem.get(url); }

  // Tier 2: IndexedDB (cross-session). null/undefined entry => miss.
  if (cacheId != null) {
    const cached = await getCached(cacheId, season, dataType);
    if (cached !== null && cached !== undefined) {
      _mem.set(url, cached);
      _stats.hits++;
      return cached;
    }
  }

  // Miss → network (throttled).
  const data = await _enqueue(() => _rawFetch(url));
  _stats.misses++;
  _mem.set(url, data); // memoize even null so a same-session retry is a hit
  // Persist only real payloads — never poison the IDB cache with a null
  // (a transient failure shouldn't stick across sessions).
  if (cacheId != null && data !== null && data !== undefined) {
    setCached(cacheId, season, dataType, data); // fire-and-forget
  }
  return data;
}
